/**
 * V4 排班结果 Excel 导出。
 *
 * 设计原则：
 * - 排班日期使用 roster_date；执行开始/结束保留站点墙钟时间，不随导出机器时区变化。
 * - 操作岗位以 operations[].positions 为唯一当前事实来源。
 * - 月历只显示“颜色 + 计入工时”，颜色语义与 V4 Web 排班矩阵一致。
 * - 所有清单均提供 Excel 自动筛选；所有人员信息均不导出工号。
 * - 打印统一使用 A3 横版，并为排班日历、工序明细和个人日历设置重复标题行。
 */
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import {
    buildScheduleExportProjection,
    ScheduleExportCoverage,
    ScheduleExportPosition,
    ScheduleExportSource,
} from './scheduleExportProjection';

export interface V4ScheduleExportData extends ScheduleExportSource {
    assignments: any[];
    shift_assignments?: any[];
    shift_definitions?: any[];
    operations?: any[];
    employee_meta?: Record<number, {
        name?: string;
        department?: string | null;
    }>;
    calendar_days?: { date: string; is_workday: boolean }[];
    run_status?: string;
    draft?: {
        exists: boolean;
        version: number;
        saved_at?: string | null;
    };
}

export interface V4ScheduleExportContext {
    hasUnsavedEdits?: boolean;
    baseSnapshot?: V4ScheduleExportData | null;
    editLabels?: string[];
    site?: {
        siteCode?: string;
        siteName?: string;
        timezone?: string;
    } | null;
    generatedAt?: Date;
}

interface ExportState {
    label: string;
    fileSuffix: string;
}

interface WallClockParts {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
}

interface ShiftInfo {
    employeeId: number;
    employeeName: string;
    department: string;
    date: string;
    shiftId: number | null;
    shiftName: string;
    shiftCode: string;
    startTime: string;
    endTime: string;
    hours: number;
    nominalHours: number;
    attendanceKind: string;
    isNight: boolean;
}

interface PersonnelStat {
    id: number;
    name: string;
    department: string;
    workDays: number;
    standardHours: number;
    actualHours: number;
    overtimeHours: number;
    operationCount: number;
    operationDays: number;
    idleDays: number;
    operationHours: number;
    utilization: number;
    maxConsecutiveWork: number;
    nightCount: number;
    nonWorkdayCount: number;
}

interface ChangeRecord {
    category: string;
    target: string;
    before: string;
    after: string;
    note: string;
}

interface OperationDayItem {
    employeeId: number;
    date: string;
    description: string;
}

type PositionRow = ScheduleExportPosition;
type CoverageSummary = ScheduleExportCoverage;

/** WXB 白色主题色板，对应 frontend/src/styles/wxb-theme.css。 */
const COLORS = {
    blue900: '082A5C',
    blue700: '0B3D7F',
    blue600: '1556B5',
    blue500: '1F6FEB',
    blue300: '9DBEF5',
    blue100: 'E6F2FB',
    blue50: 'F2F7FC',
    green700: '1F7A53',
    green600: '267F5C',
    green500: '2E9D6E',
    green300: 'A3D9BF',
    green100: 'E6F4ED',
    amber700: '8A6A1F',
    amber500: 'E8B53C',
    amber100: 'FBF1D9',
    red700: 'A2342A',
    red500: 'D6493A',
    red100: 'FBE6E3',
    ink: '0F1B2D',
    fg2: '3A4A5C',
    fg3: '5A6B7E',
    fg4: '8898A8',
    border: 'E4EAF1',
    borderStrong: 'C7D1DD',
    surface3: 'EDF1F6',
    surface2: 'F5F8FB',
    surface1: 'FAFCFE',
    white: 'FFFFFF',
} as const;

const thinBorder: Partial<ExcelJS.Borders> = {
    top: { style: 'thin', color: { argb: COLORS.border } },
    left: { style: 'thin', color: { argb: COLORS.border } },
    bottom: { style: 'thin', color: { argb: COLORS.border } },
    right: { style: 'thin', color: { argb: COLORS.border } },
};

const strongBorder: Partial<ExcelJS.Borders> = {
    top: { style: 'thin', color: { argb: COLORS.borderStrong } },
    left: { style: 'thin', color: { argb: COLORS.borderStrong } },
    bottom: { style: 'thin', color: { argb: COLORS.borderStrong } },
    right: { style: 'thin', color: { argb: COLORS.borderStrong } },
};

const WEEKDAYS = ['日', '一', '二', '三', '四', '五', '六'];

function extractLiteralParts(value: unknown): WallClockParts | null {
    if (!value) return null;
    const match = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?/.exec(String(value));
    if (!match) return null;
    return {
        year: Number(match[1]),
        month: Number(match[2]),
        day: Number(match[3]),
        hour: Number(match[4]) || 0,
        minute: Number(match[5]) || 0,
        second: Number(match[6]) || 0,
    };
}

function extractSiteWallClockParts(value: unknown, siteTimezone: string): WallClockParts | null {
    const text = String(value || '');
    const hasExplicitTimezone = value instanceof Date || /(?:Z|[+-]\d{2}:?\d{2})$/i.test(text);
    if (!hasExplicitTimezone) return extractLiteralParts(value);
    const instant = value instanceof Date ? value : new Date(text);
    if (!Number.isFinite(instant.getTime())) return null;
    try {
        const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone: siteTimezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hourCycle: 'h23',
        }).formatToParts(instant);
        const read = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((part) => part.type === type)?.value || 0);
        return {
            year: read('year'),
            month: read('month'),
            day: read('day'),
            hour: read('hour'),
            minute: read('minute'),
            second: read('second'),
        };
    } catch {
        return extractLiteralParts(value);
    }
}

/**
 * 将排班时间写入 Excel。
 * - 带 Z/offset 的求解器时间：显式转换为站点时区。
 * - 无时区的数据库 DATETIME：保持原站点墙钟。
 */
export function toScheduleExcelDateTime(value: unknown, siteTimezone = 'Asia/Shanghai'): Date | null {
    const parts = extractSiteWallClockParts(value, siteTimezone);
    if (!parts) return null;
    return new Date(Date.UTC(
        parts.year,
        parts.month - 1,
        parts.day,
        parts.hour,
        parts.minute,
        parts.second,
    ));
}

function toExcelDate(value: unknown): Date | null {
    const parts = extractLiteralParts(value);
    if (!parts) return null;
    return new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
}

function wallClockEpoch(value: unknown): number | null {
    if (!value) return null;
    const text = String(value);
    if (value instanceof Date || /(?:Z|[+-]\d{2}:?\d{2})$/i.test(text)) {
        const instant = value instanceof Date ? value : new Date(text);
        return Number.isFinite(instant.getTime()) ? instant.getTime() : null;
    }
    const parts = extractLiteralParts(value);
    return parts
        ? Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second)
        : null;
}

function formatDate(value: unknown): string {
    const parts = extractLiteralParts(value);
    if (!parts) return String(value || '').slice(0, 10);
    return `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;
}

function formatScheduleDate(value: unknown, siteTimezone: string): string {
    const parts = extractSiteWallClockParts(value, siteTimezone);
    if (!parts) return String(value || '').slice(0, 10);
    return `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;
}

function formatTime(value: unknown, siteTimezone: string): string {
    const parts = extractSiteWallClockParts(value, siteTimezone);
    if (!parts) return '-';
    return `${String(parts.hour).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}`;
}

function formatDateTimeText(value: unknown, siteTimezone: string): string {
    const date = formatScheduleDate(value, siteTimezone);
    const time = formatTime(value, siteTimezone);
    return date && time !== '-' ? `${date} ${time}` : '-';
}

function weekdayForDate(value: unknown): string {
    const date = toExcelDate(value);
    return date ? `周${WEEKDAYS[date.getUTCDay()]}` : '-';
}

function getDateRange(calendarDays?: { date: string }[]): string {
    if (!calendarDays?.length) return '-';
    const dates = calendarDays.map((day) => formatDate(day.date)).filter(Boolean).sort();
    return dates.length ? `${dates[0]} ~ ${dates[dates.length - 1]}` : '-';
}

function getExportState(data: V4ScheduleExportData, context: V4ScheduleExportContext): ExportState {
    if (context.hasUnsavedEdits) {
        return { label: '未保存修改（当前界面）', fileSuffix: '未保存修改' };
    }
    if (data.draft?.exists) {
        const version = Number(data.draft.version) || 1;
        return { label: `草稿 v${version}`, fileSuffix: `草稿v${version}` };
    }
    if (String(data.run_status || '').toUpperCase() === 'APPLIED') {
        return { label: '已应用结果', fileSuffix: '已应用' };
    }
    return { label: '求解结果', fileSuffix: '求解结果' };
}

function getEmployeeMeta(data: V4ScheduleExportData, employeeId: number): { name: string; department: string } {
    const meta = data.employee_meta?.[employeeId];
    return {
        name: String(meta?.name || `员工 ${employeeId}`),
        department: String(meta?.department || '-'),
    };
}

function normalizeShift(data: V4ScheduleExportData, shift: any): ShiftInfo | null {
    const employeeId = Number(shift.employee_id);
    if (!Number.isFinite(employeeId)) return null;
    const meta = getEmployeeMeta(data, employeeId);
    return {
        employeeId,
        employeeName: String(shift.employee_name || meta.name),
        department: meta.department,
        date: formatDate(shift.date),
        shiftId: shift.shift_id == null ? null : Number(shift.shift_id),
        shiftName: String(shift.shift_name || shift.shift_code || '-'),
        shiftCode: String(shift.shift_code || ''),
        startTime: String(shift.start_time || '00:00').slice(0, 5),
        endTime: String(shift.end_time || '00:00').slice(0, 5),
        hours: Number(shift.credited_hours ?? shift.plan_hours ?? shift.nominal_hours ?? 0),
        nominalHours: Number(shift.nominal_hours ?? 0),
        attendanceKind: String(shift.attendance_kind || shift.plan_type || '').toUpperCase(),
        isNight: Boolean(shift.is_night_shift),
    };
}

function isWorkShift(shift: ShiftInfo | null | undefined): boolean {
    if (!shift) return false;
    return shift.attendanceKind ? shift.attendanceKind === 'WORK' : shift.nominalHours > 0.01;
}

function isLeaveShift(shift: ShiftInfo | null | undefined): boolean {
    return Boolean(shift && ['PAID_LEAVE', 'UNPAID_LEAVE'].includes(shift.attendanceKind));
}

function isLongShift(shift: ShiftInfo | null | undefined): boolean {
    if (!shift) return false;
    const label = `${shift.shiftName} ${shift.shiftCode}`.toLowerCase();
    return label.includes('长白') || label.includes('长日') || label.includes('long');
}

function isNightShift(shift: ShiftInfo | null | undefined): boolean {
    if (!shift) return false;
    if (shift.isNight) return true;
    const label = `${shift.shiftName} ${shift.shiftCode}`.toLowerCase();
    return label.includes('夜') || label.includes('晚') || label.includes('night');
}

function getShiftVisual(
    shift: ShiftInfo | null | undefined,
    hasOperations: boolean,
): { bg: string; fg: string; border: string; label: string } {
    if (!shift) {
        return { bg: COLORS.surface1, fg: COLORS.fg4, border: COLORS.border, label: '-' };
    }
    if (!isWorkShift(shift)) {
        return {
            bg: isLeaveShift(shift) ? COLORS.amber100 : COLORS.surface2,
            fg: isLeaveShift(shift) ? COLORS.amber700 : COLORS.fg4,
            border: isLeaveShift(shift) ? COLORS.amber500 : COLORS.border,
            label: isLeaveShift(shift) ? '假' : '休',
        };
    }
    const hours = Number(shift.hours.toFixed(2));
    const label = `${hours}h`;
    if (!hasOperations) {
        return { bg: COLORS.green100, fg: COLORS.green700, border: COLORS.green300, label };
    }
    if (isNightShift(shift)) {
        return { bg: COLORS.red500, fg: COLORS.white, border: COLORS.red500, label };
    }
    if (isLongShift(shift)) {
        return { bg: COLORS.blue600, fg: COLORS.white, border: COLORS.blue600, label };
    }
    return { bg: COLORS.green600, fg: COLORS.white, border: COLORS.green600, label };
}

function positionKey(position: PositionRow): string {
    return `${position.operationId}:${position.positionNumber}`;
}

function shiftKey(employeeId: number, date: string): string {
    return `${employeeId}:${formatDate(date)}`;
}

function operationType(position: PositionRow): string {
    return position.operationId < 0 || position.batchCode.toUpperCase() === 'STANDALONE'
        ? '独立任务'
        : '批次工序';
}

function taskReference(position: PositionRow): string {
    if (operationType(position) === '独立任务') {
        const code = position.operationName.split(/\s+-\s+/)[0]?.trim();
        return code && code !== position.operationName ? code : `独立任务 ${Math.abs(position.operationId)}`;
    }
    return position.batchCode || '-';
}

function operationDisplayName(position: PositionRow): string {
    if (operationType(position) !== '独立任务') return position.operationName || '-';
    const parts = position.operationName.split(/\s+-\s+/);
    return parts.length > 1 ? parts.slice(1).join(' - ') : position.operationName || '-';
}

function formatAllowedWindow(position: PositionRow, siteTimezone: string): string {
    if (!position.windowStart || !position.windowEnd) return '-';
    const start = wallClockEpoch(position.windowStart);
    const end = wallClockEpoch(position.windowEnd);
    if (position.durationMinutes > 0 && start != null && end != null && end <= start) {
        return '未配置有效窗口';
    }
    return `${formatDateTimeText(position.windowStart, siteTimezone)} ~ ${formatDateTimeText(position.windowEnd, siteTimezone)}`;
}

function positionSummary(position: PositionRow, siteTimezone: string): string {
    const employee = position.assigned ? position.employeeName : '未分配';
    const roster = position.rosterDate || '-';
    const shift = position.shiftId == null ? '-' : String(position.shiftId);
    return `${employee}｜排班日 ${roster}｜班次 ${shift}｜${formatDateTimeText(position.plannedStart, siteTimezone)}~${formatDateTimeText(position.plannedEnd, siteTimezone)}`;
}

function buildChangeRecords(
    data: V4ScheduleExportData,
    context: V4ScheduleExportContext,
    positions: PositionRow[],
): { records: ChangeRecord[]; changedPositionKeys: Set<string> } {
    const records: ChangeRecord[] = [];
    const changedPositionKeys = new Set<string>();
    const base = context.baseSnapshot;
    const siteTimezone = context.site?.timezone || 'Asia/Shanghai';
    if (base) {
        const beforePositions = buildScheduleExportProjection(base).positions;
        const beforeByKey = new Map(beforePositions.map((position) => [positionKey(position), position]));
        const afterByKey = new Map(positions.map((position) => [positionKey(position), position]));
        const keys = new Set([
            ...Array.from(beforeByKey.keys()),
            ...Array.from(afterByKey.keys()),
        ]);
        keys.forEach((key) => {
            const before = beforeByKey.get(key);
            const after = afterByKey.get(key);
            if (before && after && positionSummary(before, siteTimezone) === positionSummary(after, siteTimezone)) return;
            const target = after || before;
            if (!target) return;
            changedPositionKeys.add(key);
            records.push({
                category: '岗位分配',
                target: `${taskReference(target)} / ${operationDisplayName(target)} / 岗位 ${target.positionNumber}`,
                before: before ? positionSummary(before, siteTimezone) : '无',
                after: after ? positionSummary(after, siteTimezone) : '无',
                note: '与打开结果页时的基线比较',
            });
        });

        const normalizeShifts = (source: V4ScheduleExportData) => (
            (source.shift_assignments || [])
                .map((shift) => normalizeShift(source, shift))
                .filter((shift): shift is ShiftInfo => Boolean(shift))
        );
        const beforeShifts = new Map(normalizeShifts(base).map((shift) => [shiftKey(shift.employeeId, shift.date), shift]));
        const afterShifts = new Map(
            (data.shift_assignments || [])
                .map((shift) => normalizeShift(data, shift))
                .filter((shift): shift is ShiftInfo => Boolean(shift))
                .map((shift) => [shiftKey(shift.employeeId, shift.date), shift]),
        );
        new Set([
            ...Array.from(beforeShifts.keys()),
            ...Array.from(afterShifts.keys()),
        ]).forEach((key) => {
            const before = beforeShifts.get(key);
            const after = afterShifts.get(key);
            const beforeText = before ? `${before.shiftName} / ${before.hours}h` : '无';
            const afterText = after ? `${after.shiftName} / ${after.hours}h` : '无';
            if (beforeText === afterText) return;
            const shift = after || before;
            if (!shift) return;
            records.push({
                category: '班次调整',
                target: `${shift.employeeName} / ${shift.date}`,
                before: beforeText,
                after: afterText,
                note: '与打开结果页时的基线比较',
            });
        });
    }

    (context.editLabels || []).forEach((label, index) => {
        records.push({
            category: '操作记录',
            target: `第 ${index + 1} 项`,
            before: '-',
            after: '-',
            note: label,
        });
    });
    return { records, changedPositionKeys };
}

function calculateUnionHours(intervals: Array<[number, number]>): number {
    const sorted = intervals
        .filter(([start, end]) => Number.isFinite(start) && Number.isFinite(end) && end > start)
        .sort((left, right) => left[0] - right[0]);
    if (!sorted.length) return 0;
    let total = 0;
    let [currentStart, currentEnd] = sorted[0];
    for (let index = 1; index < sorted.length; index += 1) {
        const [nextStart, nextEnd] = sorted[index];
        if (nextStart <= currentEnd) {
            currentEnd = Math.max(currentEnd, nextEnd);
        } else {
            total += currentEnd - currentStart;
            currentStart = nextStart;
            currentEnd = nextEnd;
        }
    }
    return (total + currentEnd - currentStart) / 3600000;
}

function buildOperationMaps(positions: PositionRow[], siteTimezone: string): {
    operationKeysByEmployeeDay: Map<string, Set<string>>;
    operationItemsByEmployeeDay: Map<string, OperationDayItem[]>;
} {
    const operationKeysByEmployeeDay = new Map<string, Set<string>>();
    const operationItemsByEmployeeDay = new Map<string, OperationDayItem[]>();
    positions.filter((position) => position.assigned && position.employeeId != null).forEach((position) => {
        const key = shiftKey(position.employeeId!, position.rosterDate);
        if (!operationKeysByEmployeeDay.has(key)) operationKeysByEmployeeDay.set(key, new Set());
        operationKeysByEmployeeDay.get(key)!.add(String(position.operationId));
        if (!operationItemsByEmployeeDay.has(key)) operationItemsByEmployeeDay.set(key, []);
        operationItemsByEmployeeDay.get(key)!.push({
            employeeId: position.employeeId!,
            date: position.rosterDate,
            description: `${formatTime(position.plannedStart, siteTimezone)}-${formatTime(position.plannedEnd, siteTimezone)} ${taskReference(position)} / ${operationDisplayName(position)}`,
        });
    });
    operationItemsByEmployeeDay.forEach((items) => items.sort((left, right) => left.description.localeCompare(right.description, 'zh-CN')));
    return { operationKeysByEmployeeDay, operationItemsByEmployeeDay };
}

function calculatePersonnelStats(
    data: V4ScheduleExportData,
    positions: PositionRow[],
    shifts: ShiftInfo[],
): PersonnelStat[] {
    const calendarDates = (data.calendar_days || []).map((day) => formatDate(day.date)).sort();
    const nonWorkdays = new Set(
        (data.calendar_days || []).filter((day) => !day.is_workday).map((day) => formatDate(day.date)),
    );
    const employees = new Map<number, {
        id: number;
        name: string;
        department: string;
        shifts: Map<string, ShiftInfo>;
        intervals: Array<[number, number]>;
        operationIds: Set<string>;
        operationDates: Set<string>;
    }>();
    const ensure = (id: number, name?: string, department?: string) => {
        if (!employees.has(id)) {
            const meta = getEmployeeMeta(data, id);
            employees.set(id, {
                id,
                name: name || meta.name,
                department: department || meta.department,
                shifts: new Map(),
                intervals: [],
                operationIds: new Set(),
                operationDates: new Set(),
            });
        }
        return employees.get(id)!;
    };
    shifts.forEach((shift) => {
        ensure(shift.employeeId, shift.employeeName, shift.department).shifts.set(shift.date, shift);
    });
    positions.filter((position) => position.assigned && position.employeeId != null).forEach((position) => {
        const employee = ensure(position.employeeId!, position.employeeName, position.employeeDepartment);
        const start = wallClockEpoch(position.plannedStart);
        const end = wallClockEpoch(position.plannedEnd);
        if (start != null && end != null && end > start) employee.intervals.push([start, end]);
        employee.operationIds.add(String(position.operationId));
        employee.operationDates.add(position.rosterDate);
    });

    return Array.from(employees.values()).map((employee) => {
        let consecutive = 0;
        let maxConsecutive = 0;
        let workDays = 0;
        let actualHours = 0;
        let nightCount = 0;
        let nonWorkdayCount = 0;
        let idleDays = 0;
        const dates = calendarDates.length ? calendarDates : Array.from(employee.shifts.keys()).sort();
        dates.forEach((date) => {
            const shift = employee.shifts.get(date);
            if (isWorkShift(shift)) {
                workDays += 1;
                actualHours += shift?.hours || 0;
                consecutive += 1;
                maxConsecutive = Math.max(maxConsecutive, consecutive);
                if (isNightShift(shift)) nightCount += 1;
                if (nonWorkdays.has(date)) nonWorkdayCount += 1;
                if (!employee.operationDates.has(date)) idleDays += 1;
            } else {
                consecutive = 0;
            }
        });
        const standardHours = (data.calendar_days || []).filter((day) => day.is_workday).length * 8;
        const operationHours = calculateUnionHours(employee.intervals);
        return {
            id: employee.id,
            name: employee.name,
            department: employee.department,
            workDays,
            standardHours,
            actualHours,
            overtimeHours: Math.max(0, actualHours - standardHours),
            operationCount: employee.operationIds.size,
            operationDays: employee.operationDates.size,
            idleDays,
            operationHours,
            utilization: actualHours > 0 ? operationHours / actualHours : 0,
            maxConsecutiveWork: maxConsecutive,
            nightCount,
            nonWorkdayCount,
        };
    }).sort((left, right) => left.name.localeCompare(right.name, 'zh-CN'));
}

function fill(cell: ExcelJS.Cell, color: string): void {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } };
}

function applyHeaderStyle(row: ExcelJS.Row, bgColor: string = COLORS.blue700): void {
    row.eachCell({ includeEmpty: true }, (cell) => {
        cell.font = { bold: true, color: { argb: COLORS.white }, size: 10 };
        fill(cell, bgColor);
        cell.border = strongBorder;
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    });
    row.height = 28;
}

function styleTitle(ws: ExcelJS.Worksheet, range: string, text: string): void {
    ws.mergeCells(range);
    const cell = ws.getCell(range.split(':')[0]);
    cell.value = text;
    cell.font = { bold: true, size: 17, color: { argb: COLORS.white } };
    fill(cell, COLORS.blue900);
    cell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
    ws.getRow(Number(cell.row)).height = 36;
}

function styleSubtitle(cell: ExcelJS.Cell, color: string = COLORS.blue100): void {
    cell.font = { bold: true, size: 10, color: { argb: COLORS.blue700 } };
    fill(cell, color);
    cell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1, wrapText: true };
    cell.border = thinBorder;
}

function styleDataRange(ws: ExcelJS.Worksheet, rowNumber: number, columnCount: number, zebra = false): void {
    for (let column = 1; column <= columnCount; column += 1) {
        const cell = ws.getCell(rowNumber, column);
        cell.border = thinBorder;
        cell.alignment = { vertical: 'middle', wrapText: true };
        if (zebra && !cell.fill) fill(cell, COLORS.surface1);
    }
}

function configurePrint(
    ws: ExcelJS.Worksheet,
    footerContext: string,
    options: {
        fitToWidth?: number;
        printTitlesRow?: string;
        printTitlesColumn?: string;
        printArea?: string;
    } = {},
): void {
    ws.properties.defaultRowHeight = 20;
    ws.pageSetup = {
        orientation: 'landscape',
        paperSize: 8,
        fitToPage: true,
        fitToWidth: options.fitToWidth ?? 1,
        fitToHeight: 0,
        horizontalCentered: true,
        margins: { left: 0.25, right: 0.25, top: 0.55, bottom: 0.55, header: 0.2, footer: 0.25 },
        printTitlesRow: options.printTitlesRow,
        printTitlesColumn: options.printTitlesColumn,
        printArea: options.printArea,
    };
    ws.headerFooter.oddHeader = `&L${ws.name}&R${footerContext}`;
    ws.headerFooter.oddFooter = '&LAPS V4 排班结果&C第 &P / &N 页&R&D &T';
}

function addAutoFilter(ws: ExcelJS.Worksheet, headerRow: number, lastRow: number, lastColumn: number): void {
    if (lastRow <= headerRow) return;
    ws.autoFilter = {
        from: { row: headerRow, column: 1 },
        to: { row: lastRow, column: lastColumn },
    };
}

function buildSummarySheet(
    workbook: ExcelJS.Workbook,
    data: V4ScheduleExportData,
    runId: number,
    context: V4ScheduleExportContext,
    exportState: ExportState,
    coverage: CoverageSummary,
    positions: PositionRow[],
    shifts: ShiftInfo[],
    changeRecords: ChangeRecord[],
    hasGapsSheet: boolean,
): void {
    const ws = workbook.addWorksheet('排班总览', { properties: { tabColor: { argb: COLORS.blue600 } } });
    styleTitle(ws, 'A1:H1', '排班结果报告');
    ws.mergeCells('A2:H2');
    const siteLabel = Array.from(new Set(
        [context.site?.siteCode, context.site?.siteName].filter((value): value is string => Boolean(value)),
    )).join(' / ') || '当前站点';
    ws.getCell('A2').value = `${siteLabel}  |  Run #${runId}  |  ${exportState.label}  |  ${getDateRange(data.calendar_days)}`;
    styleSubtitle(ws.getCell('A2'));
    ws.getRow(2).height = 26;

    const gaps = Math.max(0, coverage.totalPositions - coverage.assignedPositions);
    const rosterEmployees = new Set(shifts.map((shift) => shift.employeeId)).size;
    const operationEmployees = new Set(
        positions.filter((position) => position.assigned && position.employeeId != null).map((position) => position.employeeId),
    ).size;
    const standaloneOperations = new Set(
        positions.filter((position) => operationType(position) === '独立任务').map((position) => position.operationId),
    ).size;
    const kpis = [
        { label: '岗位完成率', value: coverage.completionRate, fmt: '0.0%', bg: COLORS.blue100, fg: COLORS.blue700 },
        { label: '工序覆盖率', value: coverage.coverageRate, fmt: '0.0%', bg: COLORS.green100, fg: COLORS.green700 },
        { label: '已分配岗位', value: coverage.assignedPositions, fmt: '0" 个"', bg: COLORS.surface2, fg: COLORS.ink },
        { label: '未覆盖岗位', value: gaps, fmt: '0" 个"', bg: gaps ? COLORS.red100 : COLORS.green100, fg: gaps ? COLORS.red700 : COLORS.green700 },
        { label: '工序/任务', value: coverage.totalOperations, fmt: '0" 项"', bg: COLORS.blue50, fg: COLORS.blue700 },
        { label: '其中独立任务', value: standaloneOperations, fmt: '0" 项"', bg: COLORS.amber100, fg: COLORS.amber700 },
        { label: '操作参与人员', value: operationEmployees, fmt: '0" 人"', bg: COLORS.green100, fg: COLORS.green700 },
        { label: '排班人员', value: rosterEmployees, fmt: '0" 人"', bg: COLORS.blue100, fg: COLORS.blue700 },
    ];
    kpis.forEach((kpi, index) => {
        const column = (index % 4) * 2 + 1;
        const labelRow = index < 4 ? 4 : 7;
        ws.mergeCells(labelRow, column, labelRow, column + 1);
        ws.mergeCells(labelRow + 1, column, labelRow + 1, column + 1);
        const labelCell = ws.getCell(labelRow, column);
        labelCell.value = kpi.label;
        labelCell.font = { bold: true, size: 10, color: { argb: COLORS.fg3 } };
        fill(labelCell, kpi.bg);
        labelCell.border = thinBorder;
        labelCell.alignment = { horizontal: 'center', vertical: 'middle' };
        const valueCell = ws.getCell(labelRow + 1, column);
        valueCell.value = kpi.value;
        valueCell.numFmt = kpi.fmt;
        valueCell.font = { bold: true, size: 18, color: { argb: kpi.fg } };
        fill(valueCell, kpi.bg);
        valueCell.border = thinBorder;
        valueCell.alignment = { horizontal: 'center', vertical: 'middle' };
    });
    [4, 7].forEach((row) => { ws.getRow(row).height = 22; ws.getRow(row + 1).height = 32; });

    ws.mergeCells('A10:D10');
    ws.mergeCells('E10:H10');
    ['A10', 'E10'].forEach((address) => {
        const cell = ws.getCell(address);
        cell.value = address === 'A10' ? '运行信息' : '工作表导航';
        cell.font = { bold: true, color: { argb: COLORS.white } };
        fill(cell, address === 'A10' ? COLORS.blue700 : COLORS.green700);
        cell.border = strongBorder;
        cell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
    });

    const generatedAt = context.generatedAt || new Date();
    const infoRows: Array<[string, ExcelJS.CellValue, string?]> = [
        ['结果状态', exportState.label],
        ['站点时区', context.site?.timezone || '未提供（按站点墙钟导出）'],
        ['排班周期', getDateRange(data.calendar_days)],
        ['求解耗时', Number(data.metrics.solve_time) || 0, '0.0" 秒"'],
        ['修改记录', changeRecords.length, '0" 项"'],
        ['生成时间', toScheduleExcelDateTime(generatedAt, context.site?.timezone || 'Asia/Shanghai') || generatedAt, 'yyyy-mm-dd hh:mm'],
    ];
    infoRows.forEach(([label, value, format], index) => {
        const row = 11 + index;
        ws.getCell(row, 1).value = label;
        ws.mergeCells(row, 2, row, 4);
        ws.getCell(row, 2).value = value;
        if (format) ws.getCell(row, 2).numFmt = format;
        for (let column = 1; column <= 4; column += 1) {
            const cell = ws.getCell(row, column);
            cell.border = thinBorder;
            cell.alignment = { vertical: 'middle', horizontal: column === 1 ? 'left' : 'center', wrapText: true };
            if (index % 2) fill(cell, COLORS.surface1);
        }
        ws.getCell(row, 1).font = { bold: true, color: { argb: COLORS.fg2 } };
    });

    const sheetNames = ['排班日历', '工序分配', '人员统计', '个人日历'];
    if (hasGapsSheet) sheetNames.push('覆盖缺口');
    if (changeRecords.length) sheetNames.push('变更记录');
    sheetNames.forEach((name, index) => {
        const row = 11 + index;
        ws.mergeCells(row, 5, row, 8);
        const cell = ws.getCell(row, 5);
        cell.value = { text: `打开 ${name}`, hyperlink: `#'${name}'!A1` };
        cell.font = { bold: true, color: { argb: COLORS.blue600 }, underline: true };
        cell.border = thinBorder;
        cell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
        if (index % 2) fill(cell, COLORS.surface1);
    });

    ws.mergeCells('A19:H19');
    const note = ws.getCell('A19');
    note.value = '口径：排班日期取岗位 roster_date；执行时间保留站点墙钟；排班工时取计入工时；任务占用工时按员工执行区间并集计算，重叠时段不重复累计。';
    note.font = { italic: true, size: 9, color: { argb: COLORS.fg3 } };
    fill(note, COLORS.amber100);
    note.border = thinBorder;
    note.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true, indent: 1 };
    ws.getRow(19).height = 30;

    [14, 16, 14, 16, 16, 16, 16, 16].forEach((width, index) => { ws.getColumn(index + 1).width = width; });
    ws.views = [{ showGridLines: false, zoomScale: 90 }];
    configurePrint(ws, `${siteLabel} / Run #${runId}`, { fitToWidth: 1, printArea: 'A1:H19' });
}

function buildCalendarSheet(
    workbook: ExcelJS.Workbook,
    data: V4ScheduleExportData,
    runId: number,
    context: V4ScheduleExportContext,
    exportState: ExportState,
    positions: PositionRow[],
    shifts: ShiftInfo[],
): void {
    const ws = workbook.addWorksheet('排班日历', { properties: { tabColor: { argb: COLORS.blue600 } } });
    const calendarDays = [...(data.calendar_days || [])]
        .map((day) => ({ ...day, date: formatDate(day.date) }))
        .sort((left, right) => left.date.localeCompare(right.date));
    const lastColumn = Math.max(calendarDays.length + 4, 12);
    styleTitle(ws, `A1:${ws.getColumn(lastColumn).letter}1`, `排班日历 — ${getDateRange(calendarDays)}  |  ${exportState.label}`);
    if (!calendarDays.length) {
        ws.getCell('A3').value = '暂无日历数据';
        ws.views = [{ showGridLines: false }];
        configurePrint(ws, `Run #${runId}`);
        return;
    }

    const siteTimezone = context.site?.timezone || 'Asia/Shanghai';
    const { operationKeysByEmployeeDay } = buildOperationMaps(positions, siteTimezone);
    const shiftByEmployeeDay = new Map(shifts.map((shift) => [shiftKey(shift.employeeId, shift.date), shift]));
    const employees = new Map<number, { id: number; name: string; department: string }>();
    shifts.forEach((shift) => employees.set(shift.employeeId, {
        id: shift.employeeId,
        name: shift.employeeName,
        department: shift.department,
    }));
    positions.filter((position) => position.assigned && position.employeeId != null).forEach((position) => {
        if (!employees.has(position.employeeId!)) {
            employees.set(position.employeeId!, {
                id: position.employeeId!,
                name: position.employeeName,
                department: position.employeeDepartment,
            });
        }
    });
    const employeeRows = Array.from(employees.values()).sort((left, right) => left.name.localeCompare(right.name, 'zh-CN'));

    ws.getCell('A2').value = '图例';
    ws.getCell('A2').font = { bold: true, color: { argb: COLORS.fg2 } };
    const legendItems = [
        ['有操作·日班', COLORS.green600, COLORS.white],
        ['有操作·长班', COLORS.blue600, COLORS.white],
        ['有操作·夜班', COLORS.red500, COLORS.white],
        ['在岗无操作', COLORS.green100, COLORS.green700],
        ['休息/休假', COLORS.surface2, COLORS.fg4],
    ];
    legendItems.forEach(([label, bg, fg], index) => {
        const cell = ws.getCell(2, 3 + index * 2);
        ws.mergeCells(2, 3 + index * 2, 2, 4 + index * 2);
        cell.value = label;
        cell.font = { bold: true, size: 9, color: { argb: fg } };
        fill(cell, bg);
        cell.border = strongBorder;
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });
    ws.getRow(2).height = 23;

    ws.getCell('A3').value = '有操作人数';
    ws.getCell('A4').value = '在岗无操作';
    ws.mergeCells('A3:B3');
    ws.mergeCells('A4:B4');
    [3, 4].forEach((row) => {
        ws.getCell(row, 1).font = { bold: true, color: { argb: COLORS.fg2 }, size: 9 };
        fill(ws.getCell(row, 1), row === 3 ? COLORS.green100 : COLORS.surface2);
        ws.getCell(row, 1).border = thinBorder;
        ws.getCell(row, 1).alignment = { horizontal: 'center', vertical: 'middle' };
    });
    calendarDays.forEach((day, index) => {
        let withOperations = 0;
        let idle = 0;
        employeeRows.forEach((employee) => {
            const key = shiftKey(employee.id, day.date);
            const shift = shiftByEmployeeDay.get(key);
            if (!isWorkShift(shift)) return;
            if ((operationKeysByEmployeeDay.get(key)?.size || 0) > 0) withOperations += 1;
            else idle += 1;
        });
        const column = index + 3;
        ws.getCell(3, column).value = withOperations;
        ws.getCell(4, column).value = idle;
        [3, 4].forEach((row) => {
            const cell = ws.getCell(row, column);
            cell.numFmt = '0';
            cell.border = thinBorder;
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            fill(cell, row === 3 ? COLORS.green100 : COLORS.surface2);
            cell.font = { color: { argb: row === 3 ? COLORS.green700 : COLORS.fg3 }, size: 9 };
        });
    });

    const headerRow = 5;
    ws.getCell(headerRow, 1).value = '员工';
    ws.getCell(headerRow, 2).value = '部门';
    calendarDays.forEach((day, index) => {
        const date = toExcelDate(day.date);
        const cell = ws.getCell(headerRow, index + 3);
        cell.value = date
            ? `${date.getUTCDate()}\n${WEEKDAYS[date.getUTCDay()]}`
            : day.date;
    });
    ws.getCell(headerRow, calendarDays.length + 3).value = '工时';
    ws.getCell(headerRow, calendarDays.length + 4).value = '操作日';
    applyHeaderStyle(ws.getRow(headerRow), COLORS.blue700);
    calendarDays.forEach((day, index) => {
        if (!day.is_workday) fill(ws.getCell(headerRow, index + 3), COLORS.fg3);
    });
    ws.getRow(headerRow).height = 32;

    employeeRows.forEach((employee, employeeIndex) => {
        const rowNumber = headerRow + 1 + employeeIndex;
        ws.getCell(rowNumber, 1).value = employee.name;
        ws.getCell(rowNumber, 2).value = employee.department;
        let totalHours = 0;
        let operationDays = 0;
        calendarDays.forEach((day, dayIndex) => {
            const key = shiftKey(employee.id, day.date);
            const shift = shiftByEmployeeDay.get(key);
            const hasOperations = (operationKeysByEmployeeDay.get(key)?.size || 0) > 0;
            const visual = getShiftVisual(shift, hasOperations);
            const cell = ws.getCell(rowNumber, dayIndex + 3);
            cell.value = visual.label;
            cell.font = { bold: true, size: 9, color: { argb: visual.fg } };
            fill(cell, visual.bg);
            cell.border = {
                top: { style: 'thin', color: { argb: visual.border } },
                left: { style: 'thin', color: { argb: visual.border } },
                bottom: { style: 'thin', color: { argb: visual.border } },
                right: { style: 'thin', color: { argb: visual.border } },
            };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            if (isWorkShift(shift)) totalHours += shift?.hours || 0;
            if (hasOperations) operationDays += 1;
        });
        ws.getCell(rowNumber, calendarDays.length + 3).value = Number(totalHours.toFixed(2));
        ws.getCell(rowNumber, calendarDays.length + 3).numFmt = '0.0"h"';
        ws.getCell(rowNumber, calendarDays.length + 4).value = operationDays;
        for (let column = 1; column <= calendarDays.length + 4; column += 1) {
            const cell = ws.getCell(rowNumber, column);
            if (column <= 2 || column > calendarDays.length + 2) {
                cell.border = thinBorder;
                cell.alignment = { vertical: 'middle', horizontal: column > 2 ? 'center' : 'left' };
                if (employeeIndex % 2 && !cell.fill) fill(cell, COLORS.surface1);
            }
        }
        ws.getCell(rowNumber, 1).font = { bold: true, color: { argb: COLORS.ink } };
        ws.getRow(rowNumber).height = 24;
    });

    const lastDataRow = headerRow + employeeRows.length;
    addAutoFilter(ws, headerRow, lastDataRow, calendarDays.length + 4);
    ws.getColumn(1).width = 12;
    ws.getColumn(2).width = 12;
    for (let column = 3; column <= calendarDays.length + 2; column += 1) ws.getColumn(column).width = 5.2;
    ws.getColumn(calendarDays.length + 3).width = 8;
    ws.getColumn(calendarDays.length + 4).width = 8;
    ws.views = [{
        state: 'frozen',
        xSplit: 2,
        ySplit: headerRow,
        activeCell: 'C6',
        showGridLines: false,
        zoomScale: 80,
    }];
    const site = context.site?.siteCode || context.site?.siteName || '当前站点';
    configurePrint(ws, `${site} / Run #${runId}`, {
        fitToWidth: calendarDays.length > 21 ? 2 : 1,
        printTitlesRow: '1:5',
        printTitlesColumn: 'A:B',
        printArea: `A1:${ws.getColumn(calendarDays.length + 4).letter}${lastDataRow}`,
    });
}

function buildOperationsSheet(
    workbook: ExcelJS.Workbook,
    data: V4ScheduleExportData,
    runId: number,
    context: V4ScheduleExportContext,
    exportState: ExportState,
    positions: PositionRow[],
    changedPositionKeys: Set<string>,
): void {
    const ws = workbook.addWorksheet('工序分配', { properties: { tabColor: { argb: COLORS.amber500 } } });
    const headers = [
        '排班日期', '执行开始', '执行结束', '跨日', '类型', '批次/任务', '阶段',
        '工序/任务名称', '员工', '班次', '分配状态', '执行时长(h)', '允许窗口',
        '所需资质', '可选人数', '共享组', '锁定', '修改标记', '操作ID',
    ];
    const operationGroups = new Map<string, PositionRow[]>();
    positions.forEach((position) => {
        const key = position.operationId
            ? `id:${position.operationId}`
            : [
                'fallback',
                position.batchCode,
                position.stageName,
                position.operationName,
                position.plannedStart,
                position.plannedEnd,
            ].join(':');
        if (!operationGroups.has(key)) operationGroups.set(key, []);
        operationGroups.get(key)!.push(position);
    });
    const groupedPositions = Array.from(operationGroups.values());

    styleTitle(ws, 'A1:L1', '工序与独立任务分配明细');
    for (let column = 13; column <= 19; column += 1) {
        const cell = ws.getCell(1, column);
        fill(cell, COLORS.blue900);
        cell.border = thinBorder;
    }
    ws.getCell('M1').value = '审计字段（Excel 查看）';
    ws.getCell('M1').font = { bold: true, size: 12, color: { argb: COLORS.white } };
    ws.getCell('M1').alignment = { horizontal: 'left', vertical: 'middle' };
    ws.mergeCells('A2:L2');
    ws.getCell('A2').value = `${exportState.label}  |  ${groupedPositions.length} 个工序/任务  |  ${positions.length} 个岗位  |  同一任务合并为一行，员工每人一行  |  审计字段在隐藏列 M:S`;
    ws.getCell('M2').value = '完整窗口、资质、候选人数、锁定、修改标记与操作 ID；默认打印区为 A:L';
    styleSubtitle(ws.getCell(2, 1), COLORS.amber100);
    for (let column = 13; column <= 19; column += 1) {
        const cell = ws.getCell(2, column);
        fill(cell, COLORS.surface2);
        cell.border = thinBorder;
    }
    ws.getCell('M2').font = { bold: true, size: 9, color: { argb: COLORS.fg3 } };
    ws.getRow(2).height = 28;
    headers.forEach((header, index) => { ws.getCell(3, index + 1).value = header; });
    applyHeaderStyle(ws.getRow(3), COLORS.amber700);

    const shiftNames = new Map(
        (data.shift_assignments || []).map((shift) => [Number(shift.shift_id), String(shift.shift_name || shift.shift_code || '-')]),
    );
    const siteTimezone = context.site?.timezone || 'Asia/Shanghai';
    groupedPositions.forEach((group, index) => {
        const position = group[0];
        const row = 4 + index;
        const rosterDates = Array.from(new Set(group.map((item) => item.rosterDate).filter(Boolean))).sort();
        const rosterDate = rosterDates.length === 1 ? toExcelDate(rosterDates[0]) : null;
        ws.getCell(row, 1).value = rosterDate || rosterDates.join('\n') || '-';
        if (rosterDate) ws.getCell(row, 1).numFmt = 'yyyy-mm-dd';

        const groupStarts = group
            .map((item) => ({ value: item.plannedStart, epoch: wallClockEpoch(item.plannedStart) }))
            .filter((item): item is { value: string; epoch: number } => item.epoch != null)
            .sort((left, right) => left.epoch - right.epoch);
        const groupEnds = group
            .map((item) => ({ value: item.plannedEnd, epoch: wallClockEpoch(item.plannedEnd) }))
            .filter((item): item is { value: string; epoch: number } => item.epoch != null)
            .sort((left, right) => right.epoch - left.epoch);
        const startValue = groupStarts[0]?.value || position.plannedStart;
        const endValue = groupEnds[0]?.value || position.plannedEnd;
        const start = toScheduleExcelDateTime(startValue, siteTimezone);
        const end = toScheduleExcelDateTime(endValue, siteTimezone);
        ws.getCell(row, 2).value = start || '-';
        ws.getCell(row, 3).value = end || '-';
        if (start) ws.getCell(row, 2).numFmt = 'yyyy-mm-dd hh:mm';
        if (end) ws.getCell(row, 3).numFmt = 'yyyy-mm-dd hh:mm';
        const crossDay = start && end
            ? formatScheduleDate(startValue, siteTimezone) !== formatScheduleDate(endValue, siteTimezone)
            : false;
        ws.getCell(row, 4).value = crossDay ? '是' : '否';
        ws.getCell(row, 5).value = operationType(position);
        ws.getCell(row, 6).value = taskReference(position);
        ws.getCell(row, 7).value = position.stageName || '-';
        ws.getCell(row, 8).value = operationDisplayName(position);
        const assignedPositions = group.filter((item) => item.assigned);
        const unassignedCount = group.length - assignedPositions.length;
        const assignmentLines = new Map<string, { employee: string; shift: string }>();
        assignedPositions.forEach((item) => {
            const employeeKey = item.employeeId == null ? item.employeeName : String(item.employeeId);
            if (assignmentLines.has(employeeKey)) return;
            assignmentLines.set(employeeKey, {
                employee: item.employeeName,
                shift: item.shiftId == null ? '-' : shiftNames.get(item.shiftId) || `班次 ${item.shiftId}`,
            });
        });
        const employees = Array.from(assignmentLines.values()).map((assignment) => assignment.employee);
        const employeeShifts = Array.from(assignmentLines.values()).map((assignment) => assignment.shift);
        if (unassignedCount > 0) {
            employees.push(`未分配 ×${unassignedCount}`);
            employeeShifts.push('-');
        }
        ws.getCell(row, 9).value = employees.join('\n') || `未分配 ×${group.length}`;
        ws.getCell(row, 10).value = employeeShifts.join('\n') || '-';
        ws.getCell(row, 11).value = unassignedCount === 0
            ? `已分配 ${group.length}/${group.length}`
            : assignedPositions.length > 0
                ? `部分分配 ${assignedPositions.length}/${group.length}`
                : `未分配 0/${group.length}`;
        const startMs = wallClockEpoch(startValue);
        const endMs = wallClockEpoch(endValue);
        const executionHours = startMs != null && endMs != null && endMs > startMs
            ? (endMs - startMs) / 3600000
            : (position.durationMinutes || 0) / 60;
        ws.getCell(row, 12).value = Number(executionHours.toFixed(2));
        ws.getCell(row, 12).numFmt = '0.0';
        ws.getCell(row, 13).value = formatAllowedWindow(position, siteTimezone);
        const qualifications = Array.from(new Set(
            group.map((item) => item.qualifications).filter((value) => value && value !== '-'),
        ));
        ws.getCell(row, 14).value = qualifications.join('\n') || '-';
        const eligibleCounts = group
            .map((item) => item.eligibleEmployeeCount)
            .filter((value): value is number => value != null);
        const minEligible = eligibleCounts.length ? Math.min(...eligibleCounts) : null;
        const maxEligible = eligibleCounts.length ? Math.max(...eligibleCounts) : null;
        ws.getCell(row, 15).value = minEligible == null
            ? '-'
            : minEligible === maxEligible
                ? minEligible
                : `${minEligible}–${maxEligible}`;
        const shareGroups = Array.from(new Set(group.map((item) => item.shareGroupName).filter(Boolean)));
        ws.getCell(row, 16).value = shareGroups.join('\n') || '-';
        const lockedCount = group.filter((item) => item.locked).length;
        ws.getCell(row, 17).value = lockedCount === 0 ? '否' : lockedCount === group.length ? '全部' : '部分';
        const changed = group.some((item) => changedPositionKeys.has(positionKey(item)));
        ws.getCell(row, 18).value = changed ? '是' : '否';
        ws.getCell(row, 19).value = position.operationId;

        styleDataRange(ws, row, headers.length, index % 2 === 1);
        [1, 2, 3, 4, 5, 11, 12, 15, 17, 18, 19].forEach((column) => {
            ws.getCell(row, column).alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        });
        ws.getCell(row, 9).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
        ws.getCell(row, 10).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
        if (unassignedCount > 0) {
            for (let column = 1; column <= headers.length; column += 1) fill(ws.getCell(row, column), COLORS.red100);
            ws.getCell(row, 11).font = { bold: true, color: { argb: COLORS.red700 } };
        } else {
            ws.getCell(row, 11).font = { bold: true, color: { argb: COLORS.green700 } };
        }
        if (changed) {
            fill(ws.getCell(row, 18), COLORS.amber100);
            ws.getCell(row, 18).font = { bold: true, color: { argb: COLORS.amber700 } };
        }
        ws.getRow(row).height = Math.max(30, employees.length * 15 + 8);
    });

    const lastRow = Math.max(3, 3 + groupedPositions.length);
    addAutoFilter(ws, 3, lastRow, headers.length);
    [12, 18, 18, 7, 10, 15, 14, 28, 18, 18, 16, 11, 33, 28, 10, 14, 9, 10, 12]
        .forEach((width, index) => { ws.getColumn(index + 1).width = width; });
    for (let column = 13; column <= 19; column += 1) ws.getColumn(column).hidden = true;
    ws.views = [{ state: 'frozen', ySplit: 3, activeCell: 'A4', showGridLines: false, zoomScale: 85 }];
    const site = context.site?.siteCode || context.site?.siteName || '当前站点';
    configurePrint(ws, `${site} / Run #${runId}`, {
        fitToWidth: 1,
        printTitlesRow: '1:3',
        printArea: `A1:L${lastRow}`,
    });
}

function buildPersonnelSheet(
    workbook: ExcelJS.Workbook,
    data: V4ScheduleExportData,
    runId: number,
    context: V4ScheduleExportContext,
    positions: PositionRow[],
    shifts: ShiftInfo[],
): void {
    const ws = workbook.addWorksheet('人员统计', { properties: { tabColor: { argb: COLORS.green600 } } });
    const headers = [
        '员工', '部门', '工作天数', '标准工时(h)', '实际工时(h)', '加班时长(h)', '操作数', '有操作天数',
        '在岗无操作天数', '操作占用工时(h)', '工时利用率', '最大连续工作', '夜班数', '非工作日班次',
    ];
    styleTitle(ws, `A1:${ws.getColumn(headers.length).letter}1`, '人员排班统计');
    ws.mergeCells(2, 1, 2, headers.length);
    ws.getCell(2, 1).value = '标准工时 = 周期内工作日 × 8h；实际工时使用班次“计入工时”；加班时长 = MAX(实际工时 − 标准工时, 0)。操作占用按执行区间并集计算。';
    styleSubtitle(ws.getCell(2, 1), COLORS.green100);
    headers.forEach((header, index) => { ws.getCell(3, index + 1).value = header; });
    applyHeaderStyle(ws.getRow(3), COLORS.green700);
    const stats = calculatePersonnelStats(data, positions, shifts);
    stats.forEach((stat, index) => {
        const row = 4 + index;
        const values: ExcelJS.CellValue[] = [
            stat.name,
            stat.department,
            stat.workDays,
            Number(stat.standardHours.toFixed(2)),
            Number(stat.actualHours.toFixed(2)),
            Number(stat.overtimeHours.toFixed(2)),
            stat.operationCount,
            stat.operationDays,
            stat.idleDays,
            Number(stat.operationHours.toFixed(2)),
            stat.utilization,
            stat.maxConsecutiveWork,
            stat.nightCount,
            stat.nonWorkdayCount,
        ];
        values.forEach((value, column) => { ws.getCell(row, column + 1).value = value; });
        ws.getCell(row, 6).value = {
            formula: `MAX(E${row}-D${row},0)`,
            result: Number(stat.overtimeHours.toFixed(2)),
        };
        ws.getCell(row, 11).value = {
            formula: `IF(E${row}>0,J${row}/E${row},0)`,
            result: stat.utilization,
        };
        [4, 5, 6, 10].forEach((column) => { ws.getCell(row, column).numFmt = '0.0'; });
        ws.getCell(row, 11).numFmt = '0.0%';
        styleDataRange(ws, row, headers.length, index % 2 === 1);
        for (let column = 3; column <= headers.length; column += 1) {
            ws.getCell(row, column).alignment = { horizontal: 'center', vertical: 'middle' };
        }
        const overtimeCell = ws.getCell(row, 6);
        if (stat.overtimeHours > 0) {
            fill(overtimeCell, COLORS.red100);
            overtimeCell.font = { bold: true, color: { argb: COLORS.red700 } };
        } else {
            fill(overtimeCell, COLORS.green100);
            overtimeCell.font = { color: { argb: COLORS.green700 } };
        }
        const utilizationCell = ws.getCell(row, 11);
        if (stat.utilization === 0) {
            fill(utilizationCell, COLORS.surface2);
            utilizationCell.font = { color: { argb: COLORS.fg4 } };
        } else if (stat.utilization < 0.6) {
            fill(utilizationCell, COLORS.amber100);
            utilizationCell.font = { bold: true, color: { argb: COLORS.amber700 } };
        } else if (stat.utilization <= 1) {
            fill(utilizationCell, COLORS.green100);
            utilizationCell.font = { bold: true, color: { argb: COLORS.green700 } };
        } else {
            fill(utilizationCell, COLORS.red100);
            utilizationCell.font = { bold: true, color: { argb: COLORS.red700 } };
        }
        if (stat.maxConsecutiveWork > 6) {
            fill(ws.getCell(row, 12), COLORS.red100);
            ws.getCell(row, 12).font = { bold: true, color: { argb: COLORS.red700 } };
        }
    });
    const lastRow = Math.max(3, 3 + stats.length);
    addAutoFilter(ws, 3, lastRow, headers.length);
    [13, 15, 11, 13, 13, 13, 9, 12, 16, 16, 12, 14, 9, 15]
        .forEach((width, index) => { ws.getColumn(index + 1).width = width; });
    ws.views = [{ state: 'frozen', ySplit: 3, activeCell: 'A4', showGridLines: false, zoomScale: 90 }];
    const site = context.site?.siteCode || context.site?.siteName || '当前站点';
    configurePrint(ws, `${site} / Run #${runId}`, {
        fitToWidth: 1,
        printTitlesRow: '1:3',
        printArea: `A1:N${lastRow}`,
    });
}

function buildPersonalCalendarSheet(
    workbook: ExcelJS.Workbook,
    data: V4ScheduleExportData,
    runId: number,
    context: V4ScheduleExportContext,
    positions: PositionRow[],
    shifts: ShiftInfo[],
): void {
    const ws = workbook.addWorksheet('个人日历', { properties: { tabColor: { argb: COLORS.blue500 } } });
    const headers = [
        '员工', '部门', '排班日期', '星期', '日历属性', '班次状态',
        '计入工时(h)', '班次时段', '操作数', '当日操作',
    ];
    styleTitle(ws, `A1:${ws.getColumn(headers.length).letter}1`, '个人排班与操作日历');
    ws.mergeCells(2, 1, 2, headers.length);
    ws.getCell(2, 1).value = '使用第 3 行筛选器选择一名员工后即可打印；每一天都会保留，便于核对休息、在岗无操作和具体操作。';
    styleSubtitle(ws.getCell(2, 1));
    headers.forEach((header, index) => { ws.getCell(3, index + 1).value = header; });
    applyHeaderStyle(ws.getRow(3), COLORS.blue700);

    const calendarDays = [...(data.calendar_days || [])]
        .map((day) => ({ ...day, date: formatDate(day.date) }))
        .sort((left, right) => left.date.localeCompare(right.date));
    const shiftByEmployeeDay = new Map(shifts.map((shift) => [shiftKey(shift.employeeId, shift.date), shift]));
    const siteTimezone = context.site?.timezone || 'Asia/Shanghai';
    const { operationKeysByEmployeeDay, operationItemsByEmployeeDay } = buildOperationMaps(positions, siteTimezone);
    const employees = new Map<number, { id: number; name: string; department: string }>();
    shifts.forEach((shift) => employees.set(shift.employeeId, {
        id: shift.employeeId,
        name: shift.employeeName,
        department: shift.department,
    }));
    positions.filter((position) => position.assigned && position.employeeId != null).forEach((position) => {
        if (!employees.has(position.employeeId!)) {
            employees.set(position.employeeId!, {
                id: position.employeeId!,
                name: position.employeeName,
                department: position.employeeDepartment,
            });
        }
    });
    const employeeRows = Array.from(employees.values()).sort((left, right) => left.name.localeCompare(right.name, 'zh-CN'));

    let row = 4;
    employeeRows.forEach((employee) => {
        calendarDays.forEach((day) => {
            const key = shiftKey(employee.id, day.date);
            const shift = shiftByEmployeeDay.get(key);
            const operationCount = operationKeysByEmployeeDay.get(key)?.size || 0;
            const hasOperations = operationCount > 0;
            const visual = getShiftVisual(shift, hasOperations);
            const dateValue = toExcelDate(day.date);
            ws.getCell(row, 1).value = employee.name;
            ws.getCell(row, 2).value = employee.department;
            ws.getCell(row, 3).value = dateValue || day.date;
            if (dateValue) ws.getCell(row, 3).numFmt = 'yyyy-mm-dd';
            ws.getCell(row, 4).value = weekdayForDate(day.date);
            ws.getCell(row, 5).value = day.is_workday ? '工作日' : '非工作日';
            let state = '未排班';
            if (shift) {
                if (!isWorkShift(shift)) state = isLeaveShift(shift) ? '休假' : '休息';
                else if (!hasOperations) state = '在岗无操作';
                else if (isNightShift(shift)) state = '有操作·夜班';
                else if (isLongShift(shift)) state = '有操作·长班';
                else state = '有操作·日班';
            }
            ws.getCell(row, 6).value = state;
            ws.getCell(row, 7).value = shift ? Number(shift.hours.toFixed(2)) : 0;
            ws.getCell(row, 7).numFmt = '0.0';
            ws.getCell(row, 8).value = shift && isWorkShift(shift) ? `${shift.startTime}-${shift.endTime}` : '-';
            ws.getCell(row, 9).value = operationCount;
            ws.getCell(row, 10).value = (operationItemsByEmployeeDay.get(key) || [])
                .map((item) => item.description)
                .join('\n') || '-';
            styleDataRange(ws, row, headers.length, row % 2 === 1);
            [3, 4, 5, 6, 7, 8, 9].forEach((column) => {
                ws.getCell(row, column).alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            });
            fill(ws.getCell(row, 6), visual.bg);
            ws.getCell(row, 6).font = { bold: true, color: { argb: visual.fg } };
            ws.getRow(row).height = operationCount > 1 ? Math.min(60, 18 + operationCount * 12) : 24;
            row += 1;
        });
    });
    const lastRow = Math.max(3, row - 1);
    addAutoFilter(ws, 3, lastRow, headers.length);
    [13, 15, 13, 8, 11, 15, 12, 13, 9, 58]
        .forEach((width, index) => { ws.getColumn(index + 1).width = width; });
    ws.views = [{ state: 'frozen', ySplit: 3, activeCell: 'A4', showGridLines: false, zoomScale: 90 }];
    const site = context.site?.siteCode || context.site?.siteName || '当前站点';
    configurePrint(ws, `${site} / Run #${runId}`, {
        fitToWidth: 1,
        printTitlesRow: '1:3',
        printArea: `A1:J${lastRow}`,
    });
}

function buildGapsSheet(
    workbook: ExcelJS.Workbook,
    runId: number,
    context: V4ScheduleExportContext,
    gaps: PositionRow[],
): void {
    if (!gaps.length) return;
    const ws = workbook.addWorksheet('覆盖缺口', { properties: { tabColor: { argb: COLORS.red500 } } });
    const headers = ['排班日期', '类型', '批次/任务', '工序/任务名称', '岗位', '允许窗口', '所需资质', '可选人数', '原因'];
    styleTitle(ws, `A1:${ws.getColumn(headers.length).letter}1`, '覆盖缺口');
    ws.mergeCells(2, 1, 2, headers.length);
    ws.getCell(2, 1).value = `共 ${gaps.length} 个未覆盖岗位；可直接按日期、类型、资质或可选人数筛选。`;
    styleSubtitle(ws.getCell(2, 1), COLORS.red100);
    headers.forEach((header, index) => { ws.getCell(3, index + 1).value = header; });
    applyHeaderStyle(ws.getRow(3), COLORS.red700);
    gaps.forEach((gap, index) => {
        const row = 4 + index;
        const date = toExcelDate(gap.rosterDate);
        ws.getCell(row, 1).value = date || gap.rosterDate || '-';
        if (date) ws.getCell(row, 1).numFmt = 'yyyy-mm-dd';
        ws.getCell(row, 2).value = operationType(gap);
        ws.getCell(row, 3).value = taskReference(gap);
        ws.getCell(row, 4).value = operationDisplayName(gap);
        ws.getCell(row, 5).value = gap.positionNumber;
        ws.getCell(row, 6).value = formatAllowedWindow(gap, context.site?.timezone || 'Asia/Shanghai');
        ws.getCell(row, 7).value = gap.qualifications;
        ws.getCell(row, 8).value = gap.eligibleEmployeeCount == null ? '-' : gap.eligibleEmployeeCount;
        ws.getCell(row, 9).value = gap.eligibleEmployeeCount === 0 ? '无满足条件人员' : '未匹配到人员';
        styleDataRange(ws, row, headers.length, index % 2 === 1);
        for (let column = 1; column <= headers.length; column += 1) {
            if (index % 2 === 0) fill(ws.getCell(row, column), COLORS.red100);
        }
    });
    const lastRow = 3 + gaps.length;
    addAutoFilter(ws, 3, lastRow, headers.length);
    [13, 11, 16, 28, 8, 36, 28, 10, 18].forEach((width, index) => { ws.getColumn(index + 1).width = width; });
    ws.views = [{ state: 'frozen', ySplit: 3, activeCell: 'A4', showGridLines: false, zoomScale: 90 }];
    const site = context.site?.siteCode || context.site?.siteName || '当前站点';
    configurePrint(ws, `${site} / Run #${runId}`, {
        fitToWidth: 1,
        printTitlesRow: '1:3',
        printArea: `A1:I${lastRow}`,
    });
}

function buildChangesSheet(
    workbook: ExcelJS.Workbook,
    runId: number,
    context: V4ScheduleExportContext,
    records: ChangeRecord[],
): void {
    if (!records.length) return;
    const ws = workbook.addWorksheet('变更记录', { properties: { tabColor: { argb: COLORS.amber500 } } });
    const headers = ['序号', '类别', '对象', '修改前', '修改后', '说明'];
    styleTitle(ws, 'A1:F1', '当前导出变更记录');
    ws.mergeCells('A2:F2');
    ws.getCell('A2').value = '该表比较“当前界面结果”与打开结果页时的基线；已应用后重新加载时，当前结果会成为新的基线。';
    styleSubtitle(ws.getCell('A2'), COLORS.amber100);
    headers.forEach((header, index) => { ws.getCell(3, index + 1).value = header; });
    applyHeaderStyle(ws.getRow(3), COLORS.amber700);
    records.forEach((record, index) => {
        const row = 4 + index;
        [index + 1, record.category, record.target, record.before, record.after, record.note]
            .forEach((value, column) => { ws.getCell(row, column + 1).value = value; });
        styleDataRange(ws, row, headers.length, index % 2 === 1);
        ws.getRow(row).height = 34;
    });
    const lastRow = 3 + records.length;
    addAutoFilter(ws, 3, lastRow, headers.length);
    [8, 12, 32, 42, 42, 30].forEach((width, index) => { ws.getColumn(index + 1).width = width; });
    ws.views = [{ state: 'frozen', ySplit: 3, activeCell: 'A4', showGridLines: false, zoomScale: 90 }];
    const site = context.site?.siteCode || context.site?.siteName || '当前站点';
    configurePrint(ws, `${site} / Run #${runId}`, {
        fitToWidth: 1,
        printTitlesRow: '1:3',
        printArea: `A1:F${lastRow}`,
    });
}

/** 创建可测试的 V4 排班工作簿。 */
export function buildV4ScheduleWorkbook(
    data: V4ScheduleExportData,
    runId: number,
    context: V4ScheduleExportContext = {},
): ExcelJS.Workbook {
    const workbook = new ExcelJS.Workbook();
    const exportState = getExportState(data, context);
    const projection = buildScheduleExportProjection(data);
    const positions = projection.positions;
    const shifts = (data.shift_assignments || [])
        .map((shift) => normalizeShift(data, shift))
        .filter((shift): shift is ShiftInfo => Boolean(shift));
    const { records, changedPositionKeys } = buildChangeRecords(data, context, positions);
    const generatedAt = context.generatedAt || new Date();

    workbook.creator = 'APS V4 排班系统';
    workbook.company = context.site?.siteName || 'APS';
    workbook.subject = `V4 排班结果 Run #${runId}`;
    workbook.title = `排班结果 V4-${runId}`;
    workbook.description = `${exportState.label}；排班日期采用 roster_date；时间按站点墙钟导出。`;
    workbook.created = generatedAt;
    workbook.modified = generatedAt;
    workbook.calcProperties.fullCalcOnLoad = true;

    buildSummarySheet(
        workbook,
        data,
        runId,
        context,
        exportState,
        projection.coverage,
        positions,
        shifts,
        records,
        projection.gaps.length > 0,
    );
    buildCalendarSheet(workbook, data, runId, context, exportState, positions, shifts);
    buildOperationsSheet(workbook, data, runId, context, exportState, positions, changedPositionKeys);
    buildPersonnelSheet(workbook, data, runId, context, positions, shifts);
    buildPersonalCalendarSheet(workbook, data, runId, context, positions, shifts);
    buildGapsSheet(workbook, runId, context, projection.gaps);
    buildChangesSheet(workbook, runId, context, records);
    return workbook;
}

function safeFilePart(value: string): string {
    return value.replace(/[\\/:*?"<>|]/g, '-').replace(/\s+/g, '').slice(0, 40);
}

/** 导出 V4 排班结果为 Excel 文件。 */
export async function exportV4ScheduleToExcel(
    data: V4ScheduleExportData,
    runId: number,
    context: V4ScheduleExportContext = {},
): Promise<void> {
    const workbook = buildV4ScheduleWorkbook(data, runId, context);
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const exportState = getExportState(data, context);
    const site = safeFilePart(context.site?.siteCode || context.site?.siteName || '');
    const range = (data.calendar_days || []).map((day) => formatDate(day.date)).sort();
    const rangeSuffix = range.length ? `${range[0]}_${range[range.length - 1]}` : '';
    const parts = ['排班结果', site, `V4-${runId}`, rangeSuffix, exportState.fileSuffix].filter(Boolean);
    saveAs(blob, `${parts.join('_')}.xlsx`);
}
